import {  makeStyles, shorthands } from '@fluentui/react-components';
import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Brain, Eye, CheckCircle } from 'lucide-react';
import SyButton from '../Common/SyButton';
import { WIKI_URLS } from '../../constants/wikiUrls';

export interface TermsOfUseProps {
    open: boolean;
    onAccept: () => void | Promise<void>;
    onDecline?: () => void | Promise<void>;
}

const useStyles = makeStyles({
    backdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
    },
    backdropVisible: {
        opacity: 1,
    },
    container: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1001,
        transform: 'translateY(100%)',
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '@media (max-width: 768px)': {
            height: '665px',
            maxHeight: '665px',
        }
    },
    containerVisible: {
        transform: 'translateY(0)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.padding('50px', '24px', '16px'),
        position: 'relative',
        '@media (max-width: 768px)': {
            ...shorthands.padding('16px', '16px', '8px'),
        }
    },
    title: {
        fontSize: '24px',
        // color: 'var(--color-gray-900)',
        fontWeight: 600,
        margin: 0,
        '@media (max-width: 768px)': {
            fontSize: '22px',
        }
    },
    content: {
        ...shorthands.padding('24px, 24px, 0px'),
        overflowY: 'auto',
        flexGrow: 1,
        '& a': {
            color: 'var(--color-primary-500)',
        },
        '@media (max-width: 768px)': {
            ...shorthands.padding('10px'),
        }
    },
    infoContent: {
        flex: 1,
    },
    infoTitle: {
        fontSize: '16px',
        // color: 'var(--color-gray-900)',
        fontWeight: 600,
        marginBottom: '8px',
        '& a': {
            color: 'var(--color-primary-500)',
            textDecoration: 'none',
        },
        '@media (max-width: 768px)': {
            fontSize: '15px',
            marginBottom: '6px',
        }
    },

    infoText: {
        fontSize: '14px',
        color: 'var(--color-gray-500)',
        lineHeight: '1.5',

        '@media (max-width: 768px)': {
            fontSize: '14px',
        }
    },
    infoBox: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap('12px'),
        ...shorthands.padding('16px'),
        marginBottom: '16px',
        maxWidth: '736px',
        marginLeft: 'auto',
        marginRight: 'auto',
        '@media (max-width: 768px)': {
            ...shorthands.padding('8px'),
            ...shorthands.gap('8px'),
            marginBottom: '1px',
            paddingBottom: '1px',
        }
    },
    infoIcon: {
        fontSize: '24px',
        color: 'var(--color-primary-500)',
        flexShrink: 0,
        '@media (max-width: 768px)': {
            fontSize: '20px',
        }
    },
    separator: {
        height: '1px',
        backgroundColor: 'var(--color-gray-200)',
        marginTop: '16px',
        marginBottom: '16px',
        ...shorthands.border('none'),
        maxWidth: '736px',
        marginLeft: 'auto',
        marginRight: 'auto',
        '@media (max-width: 768px)': {
            marginTop: '6px',
            marginBottom: '6px',
        }
    },
    footer: {
        display: 'flex',
        ...shorthands.gap('12px'),
        ...shorthands.padding('1px', '24px', '50px'),
        justifyContent: 'center',
        '@media (max-width: 768px)': {
            height: '50px',
            borderTop: '1px solid var(--color-gray-200)',
            ...shorthands.padding('20px', '16px'),
            ...shorthands.gap('8px'),
        }
    },
    footerContainer: {
        display: 'flex',
        width: '100%',
        maxWidth: '736px',
        ...shorthands.gap('12px')
    },
    btnContainer: {
        minWidth: '47%',
    }

});
const useDarkMode = () => {
    const [isDarkMode, setIsDarkMode] = useState(document.body.getAttribute('data-theme') === 'dark');

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.body.getAttribute('data-theme') === 'dark');
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    return isDarkMode;
};
const TermsOfUse: FC<TermsOfUseProps> = ({ open, onAccept, onDecline }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [processing, setProcessing] = useState(false);    
    const isDarkMode = useDarkMode();

    useEffect(() => {
        if (open) {
            // Delay to allow component mounting before animation
            setTimeout(() => setVisible(true), 10);
        } else {
            setVisible(false);
        }
    }, [open]);

    const handleAccept = async () => {
        setProcessing(true);
        setVisible(false); // Trigger slide down animation

        // Wait for animation to complete before calling onAccept
        setTimeout(async () => {
            try {
                await Promise.resolve(onAccept());
            } catch (error) {
                console.error('Error accepting terms:', error);
            } finally {
                setProcessing(false);
            }
        }, 400);
    };

    const handleDecline = async () => {
        if (onDecline) {
            try {
                await Promise.resolve(onDecline());
            } catch (error) {
                console.error('Error declining terms:', error);
            }
        }
        setVisible(false);
    };

    if (!open) return null;

    return (
        <>
            <div
                className={`${classes.backdrop} ${visible ? classes.backdropVisible : ''}`}
            />
            <div className={`${classes.container} ${visible ? classes.containerVisible : ''}`}>
                <div className={classes.header}>
                    <h2 className={classes.title}>
                        {t('tou.title', 'Terms of Use')}</h2>
                </div>

                <div className={classes.content}>
                    {/* Locker Box - Export Control */}
                    <div className={classes.infoBox}>
                        <Lock className={classes.infoIcon} />
                        <div className={classes.infoContent}>
                            <div className={classes.infoTitle}>
                                {t('tou.locker.title', 'LockerTitle')}
                            </div>
                            <div className={classes.infoText}>
                                {t('tou.locker.content', 'LockerContent')}
                            </div>
                        </div>
                    </div>

                    <div className={classes.separator} />

                    {/* Info Box - Critical Mind */}
                    <div className={classes.infoBox}>
                        <Brain className={classes.infoIcon} />
                        <div className={classes.infoContent}>
                            <div className={classes.infoTitle}>
                                {t('tou.brain.title', 'BrainTitle')}
                            </div>
                            <div className={classes.infoText}>
                                {t('tou.brain.content', 'BrainContent')}
                            </div>
                        </div>
                    </div>

                    <div className={classes.separator} />

                    {/* Info Box - Policies */}
                    <div className={classes.infoBox}>
                        <Eye className={classes.infoIcon} />
                        <div className={classes.infoContent}>
                            <div className={classes.infoTitle}>
                                {t('tou.eyes.title', 'EyesTitle')}
                            </div>
                        </div>
                    </div>

                    <div className={classes.separator} />

                    {/* Acknowledgement */}
                    <div className={classes.infoBox}>
                        <CheckCircle className={classes.infoIcon} />
                        <div className={classes.infoContent}>
                            <div className={classes.infoTitle}>
                                {t('tou.circleCheck.title', 'CircleCheckTitle')} {' '}
                                <a href={WIKI_URLS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
                                    {t('tou.circleCheck.titleLink', 'CircleCheckTitleLink')}
                                </a>
                                .
                            </div>
                        </div>
                    </div>
                </div>

                <div className={classes.footer}>
                    <div className={classes.footerContainer}>
                        <div className={classes.btnContainer}>
                            <SyButton
                                appearance= {isDarkMode ? "tertiary" : "outline"}
                                onClick={handleDecline}
                                disabled={processing}
                                size="extra-large"
                            >
                                {t('tou.decline', 'Decline')}
                            </SyButton>
                        </div>
                        <div className={classes.btnContainer}>
                            <SyButton
                                appearance="primary"
                                onClick={handleAccept}
                                disabled={processing}
                                size="extra-large"
                            >
                                {processing ? t('loading') : t('tou.accept', 'Accept')}
                            </SyButton>
                        </div>
                    </div>
                </div>
            </div >
        </>
    );
};

export default TermsOfUse;
